import { AxiosInstance } from "axios";
import { useContext, useEffect, useState } from "react";
import httpClientFactory from "../../config/httpConfig";
import { AppConfig } from "../../context/context";
import { IOrganizationModel, numberToOrgType } from "./organizations.model";

const TENANTS_ENDPOINT = "v1/DevTenants";

interface UpdateOrgOpts {
  orgId: string;
  displayName?: string;
  locale?: string;
  adminEmail?: string;
  mfa?: any;
}

interface IOrganizationService {
  getAll: (...args: any[]) => Promise<any>;
  getOrg: (orgName: string) => Promise<any>;
  createOrg: (...args: any[]) => Promise<any>;
  updateOrg: ({ orgId, displayName, locale, adminEmail, mfa }: UpdateOrgOpts) => Promise<any>;
  delete: (...args: any[]) => Promise<any>;
  getOrgLimit: () => Promise<any>;
  resendEmail: (orgName: string) => Promise<any>;
  updateOrgEmail: (orgId: any, locale: string) => Promise<any>;
  putOrgMfa: (orgName: string, data: any) => Promise<any>;
}

export const OrganizationService = (config: any, httpClient?: AxiosInstance): IOrganizationService => {
  const http: AxiosInstance = httpClient || httpClientFactory(config);
  const adminTenantId = config.tenantId;


  return {
    getAll: async (
      page = 1,
      size = 100,
      filter = "",
      statusFilter: number
    ) => {
      return await http
        .get(
          `${TENANTS_ENDPOINT}/${adminTenantId}/children?page=${page}&size=${size}&filter=${filter}&statusFilter=${statusFilter}`
        )
        .then((response: any) => mapModelToOrganizationList(response));
    },

    getOrg: async (orgName: string) => {
      return await http
        .get(`${TENANTS_ENDPOINT}/${orgName}`)
        .then((response: any) => tenantViewToOrg(response));
    },

    createOrg: async (organization: any) => {
      return await http
        .post(`${TENANTS_ENDPOINT}/${adminTenantId}/children`, organization)
        .then((response: any) => response.data);
    },

    updateOrg: ({ orgId, displayName, locale, adminEmail, mfa }: UpdateOrgOpts) => {
      const payload = mfa != null
        ? { enableMFA: mfa.enableMFA, typeMFA: mfa.typeMFA }
        : { displayName, locale, adminEmail };
      return http.put(`${TENANTS_ENDPOINT}/${orgId}`, payload);
    },

    delete: (orgId: any) => {
      return http.delete(`${TENANTS_ENDPOINT}/${orgId}`);
    },

    getOrgLimit: () => {
      return http.get(`${TENANTS_ENDPOINT}/${adminTenantId}/limits`);
    },

    resendEmail: (orgName: string) => {
      return http.post(`${TENANTS_ENDPOINT}/${orgName}/send-activation-notification`);
    },
    updateOrgEmail: (orgId: any, locale = "") => {
      return http.put(`${TENANTS_ENDPOINT}/${orgId}`, { locale });
    },
    putOrgMfa: (orgName: string, data: any) => {
      return http.put(`${TENANTS_ENDPOINT}/${orgName}`, data);
    },
  };
};

const tenantViewToOrg = (org: any): IOrganizationModel => {
  return {
    id: org.name,
    name: org.name,
    displayName: org.displayName || org.name,
    status: org.isActivated,
    type: numberToOrgType(org.type),
    email: org.adminEmail || "",
    url: org.url,
    locale: org.locale,
    isActivated: org.isActivated,
    created: org.created || "-",
    enableMFA: org.enableMFA,
    typeMFA: org.typeMFA
  };
};

const mapModelToOrganizationList = (data: any) => {
  const rows: any[] = data.tenantViews.map((org: any) => tenantViewToOrg(org));
  return {
    tenantViews: rows,
    count: data.count,
  };
};

export function useService() {
  const { config } = useContext(AppConfig) || {};

  if (!config) {
    throw new Error("AppConfig context is not properly initialized.");
  }
  const [orgService, setOrgService] = useState<IOrganizationService>({} as IOrganizationService);

  if (!config) {
    throw new Error("useServices hook must be used inside AppConfig context");
  }

  useEffect(() => {
    const { baseUrl, tenantId, token } = config;
    if (!baseUrl || !tenantId || !token) return;

    setOrgService(OrganizationService({ baseUrl, tenantId, token }));
  }, [config]);

  return { orgService };
}

export default OrganizationService;
